import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import PricingCard from '../../components/common/PricingCard';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

export default function PricingPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const { accessToken, user, isPaid } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isPaid) {
      navigate('/dashboard');
      return;
    }
    
    fetchProducts();
  }, [isPaid, navigate]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/payment/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load pricing. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (edition) => {
    if (!accessToken) {
      toast.error('Please sign in first');
      navigate('/login');
      return;
    }
    
    if (!user?.emailVerified) {
      toast.error('Please verify your email first');
      navigate('/verify-email', { state: { email: user?.email } });
      return;
    }
    
    setSelectedEdition(edition);
    
    try {
      const response = await axios.post(
        `${API}/payment/create-checkout-session`,
        {
          edition,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` }
        }
      );
      
      // Redirect to Stripe checkout or success page
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setSelectedEdition(null);
    }
  };

  const getProductData = (edition) => {
    const product = products.find(p => p.edition === edition);
    return product || {
      name: edition === 'client' ? 'Client Edition' : 'Community Edition',
      price: edition === 'client' ? 395 : 595,
      description: edition === 'client' 
        ? 'Exclusive pricing for existing KGOB clients'
        : 'Full review for new customers'
    };
  };

  return (
    <div 
      className="min-h-[80vh] py-12 px-4"
      data-testid="plan-selection-page"
    >
      <div className="mx-auto max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-semibold text-[#1A365D]">
            Choose Your Plan
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Select the plan that's right for you. One-time payment, no subscriptions.
          </p>
          {user && (
            <p className="mt-2 text-sm text-[#00B4B4]">
              Signed in as {user.email}
            </p>
          )}
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <PricingCard
            edition="client"
            name={getProductData('client').name}
            price={getProductData('client').price}
            description={getProductData('client').description}
            note="33% loyalty discount"
            onSelect={handleSelectPlan}
            loading={selectedEdition === 'client'}
            disabled={selectedEdition !== null}
          />
          <PricingCard
            edition="community"
            name={getProductData('community').name}
            price={getProductData('community').price}
            description={getProductData('community').description}
            onSelect={handleSelectPlan}
            loading={selectedEdition === 'community'}
            disabled={selectedEdition !== null}
          />
        </div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-sm text-slate-500">
            Secure payment powered by Stripe. Your payment information is never stored on our servers.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
